import { useState } from 'react';
import type { SiteRoute } from './common';

// Egyszerű sor-szerkesztők a Beállítások űrlaphoz (lista, kulcs/érték, útvonal-szabályok táblázata + JSON nézet).

export function ListEditor({ value, onChange, placeholder, addLabel = '+ Új sor' }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string; addLabel?: string }) {
  return (
    <div className="a-site-rows">
      {value.map((v, i) => (
        <div key={i} className="a-site-row">
          <input className="a-input" value={v} placeholder={placeholder} onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))} />
          <button type="button" className="a-site-mini danger" title="Törlés" onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <button type="button" className="a-btn sm ghost a-site-add" onClick={() => onChange([...value, ''])}>{addLabel}</button>
    </div>
  );
}

// Kulcs/érték sorok: belül sor-tömb (gépelés közben a kulcs nem ugrál), kifelé objektum (üres kulcs kimarad).
export function KeyValueEditor({ value, onChange, quick, keyPlaceholder = 'kulcs', valuePlaceholder = 'érték' }: { value: Record<string, string>; onChange: (v: Record<string, string>) => void; quick?: { label: string; key: string; value: string; title?: string }[]; keyPlaceholder?: string; valuePlaceholder?: string }) {
  const [rows, setRows] = useState<[string, string][]>(() => Object.entries(value ?? {}));
  const emit = (next: [string, string][]) => { setRows(next); onChange(Object.fromEntries(next.filter(([k]) => k.trim()).map(([k, v]) => [k.trim(), v]))); };
  return (
    <div className="a-site-rows">
      {rows.map(([k, v], i) => (
        <div key={i} className="a-site-row kv">
          <input className="a-input mono" value={k} placeholder={keyPlaceholder} onChange={(e) => emit(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))} />
          <input className="a-input mono" value={v} placeholder={valuePlaceholder} onChange={(e) => emit(rows.map((r, j) => (j === i ? [r[0], e.target.value] : r)))} />
          <button type="button" className="a-site-mini danger" title="Törlés" onClick={() => emit(rows.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div className="a-site-rowbtns">
        <button type="button" className="a-btn sm ghost a-site-add" onClick={() => setRows([...rows, ['', '']])}>+ Új sor</button>
        {quick?.map((q) => (
          <button key={q.label} type="button" className="a-chip a-chipbtn" title={q.title ?? `${q.key}: ${q.value}`} disabled={rows.some(([k]) => k === q.key)}
            onClick={() => emit([...rows, [q.key, q.value]])}>+ {q.label}</button>
        ))}
      </div>
    </div>
  );
}

// Útvonal-szabályok: sorrend számít (az edge sorban illeszti). Táblázat a gyakori mezőkkel; a JSON nézetben minden (pl. fejlécek).
export function RoutesEditor({ value, onChange }: { value: SiteRoute[]; onChange: (v: SiteRoute[]) => void }) {
  const [json, setJson] = useState<string | null>(null);
  const [jsonErr, setJsonErr] = useState('');
  const set = (i: number, patch: Partial<SiteRoute>) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, d: number) => { const n = [...value]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x!); onChange(n); };
  const clean = (r: SiteRoute): SiteRoute => Object.fromEntries(Object.entries(r).filter(([, v]) => v !== '' && v !== undefined && v !== false)) as unknown as SiteRoute;

  if (json != null) {
    return (
      <div className="a-site-rows">
        <textarea className="a-input mono a-site-code" rows={Math.min(18, Math.max(6, json.split('\n').length + 1))} value={json} onChange={(e) => { setJson(e.target.value); setJsonErr(''); }} spellCheck={false} />
        {jsonErr && <div className="a-site-msg bad">{jsonErr}</div>}
        <div className="a-site-rowbtns">
          <button type="button" className="a-btn sm pri" onClick={() => { try { const v = JSON.parse(json) as unknown; if (!Array.isArray(v)) throw new Error('tömb kell: [ {path, to, …} ]'); onChange(v as SiteRoute[]); setJson(null); } catch (e) { setJsonErr(`Érvénytelen JSON: ${e instanceof Error ? e.message : ''}`); } }}>Alkalmaz</button>
          <button type="button" className="a-btn sm ghost" onClick={() => { setJson(null); setJsonErr(''); }}>Vissza a táblázathoz</button>
        </div>
      </div>
    );
  }
  return (
    <div className="a-site-rows">
      <div className="a-site-tablewrap">
        <table className="a-site-table a-site-routes">
          <thead><tr><th>Útvonal</th><th>Cél</th><th title="Pufferelés nélkül (SSE, websocket, stream)">no_buffer</th><th>timeout</th><th>body_limit</th><th /></tr></thead>
          <tbody>
            {value.map((r, i) => (
              <tr key={i}>
                <td><input className="a-input mono" value={r.path ?? ''} placeholder="/api/*" onChange={(e) => set(i, { path: e.target.value })} /></td>
                <td><input className="a-input mono" value={r.to ?? ''} placeholder="port:3800 | static" list="a-site-route-to" onChange={(e) => set(i, { to: e.target.value })} /></td>
                <td className="c"><input type="checkbox" checked={Boolean(r.no_buffer)} onChange={(e) => set(i, { no_buffer: e.target.checked })} /></td>
                <td><input className="a-input mono" value={r.timeout ?? ''} placeholder="3600s" onChange={(e) => set(i, { timeout: e.target.value })} /></td>
                <td><input className="a-input mono" value={r.body_limit ?? ''} placeholder="600MB" onChange={(e) => set(i, { body_limit: e.target.value })} /></td>
                <td className="acts">
                  <button type="button" className="a-site-mini" title="Feljebb" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button type="button" className="a-site-mini danger" title="Törlés" onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button>
                  {r.headers && Object.keys(r.headers).length > 0 && <span className="a-chip" title={JSON.stringify(r.headers)}>+fejléc</span>}
                </td>
              </tr>
            ))}
            {value.length === 0 && <tr className="empty"><td colSpan={6}>Nincs külön szabály — minden kérés az alap célra megy (port vagy statikus gyökér).</td></tr>}
          </tbody>
        </table>
        <datalist id="a-site-route-to"><option value="static" /><option value="port:3000" /></datalist>
      </div>
      <div className="a-site-rowbtns">
        <button type="button" className="a-btn sm ghost a-site-add" onClick={() => onChange([...value, { path: '', to: '' }])}>+ Új szabály</button>
        <button type="button" className="a-btn sm ghost" onClick={() => setJson(JSON.stringify(value.map(clean), null, 2))}>JSON nézet</button>
      </div>
    </div>
  );
}

// Kötelező fejlécek: útvonal → fejlécnevek (vesszővel). Belül sor-tömb, kifelé { "/": ["Cross-Origin-Opener-Policy", …] }.
const ISOLATION_HEADERS = ['Cross-Origin-Opener-Policy', 'Cross-Origin-Embedder-Policy', 'Cross-Origin-Resource-Policy'];
export function HeaderRulesEditor({ value, onChange }: { value: Record<string, string[]>; onChange: (v: Record<string, string[]>) => void }) {
  const [rows, setRows] = useState<[string, string][]>(() => Object.entries(value ?? {}).map(([k, v]) => [k, v.join(', ')]));
  const split = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
  const emit = (next: [string, string][]) => {
    setRows(next);
    const out: Record<string, string[]> = {};
    for (const [p, h] of next) { const k = p.trim(); if (!k) continue; out[k] = [...new Set([...(out[k] ?? []), ...split(h)])]; }
    onChange(out);
  };
  // Gyors gomb: a három izolációs fejléc a /-re (meglévő /-sorhoz hozzáfűzve, ismétlés nélkül)
  const addIsolation = () => {
    const i = rows.findIndex(([p]) => p.trim() === '/');
    if (i < 0) { emit([...rows, ['/', ISOLATION_HEADERS.join(', ')]]); return; }
    const have = split(rows[i]![1]);
    emit(rows.map((r, j) => (j === i ? [r[0], [...have, ...ISOLATION_HEADERS.filter((x) => !have.some((y) => y.toLowerCase() === x.toLowerCase()))].join(', ')] : r)));
  };
  const hasIsolation = rows.some(([p, h]) => p.trim() === '/' && ISOLATION_HEADERS.every((x) => split(h).some((y) => y.toLowerCase() === x.toLowerCase())));
  return (
    <div className="a-site-rows">
      {rows.map(([p, h], i) => (
        <div key={i} className="a-site-row kv a-site-hdrrow">
          <input className="a-input mono" value={p} placeholder="/" title="Útvonal a valódi domainen" onChange={(e) => emit(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))} />
          <input className="a-input mono" value={h} placeholder="Cross-Origin-Opener-Policy, Content-Security-Policy" title="Fejlécnevek vesszővel" onChange={(e) => emit(rows.map((r, j) => (j === i ? [r[0], e.target.value] : r)))} />
          <button type="button" className="a-site-mini danger" title="Törlés" onClick={() => emit(rows.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div className="a-site-rowbtns">
        <button type="button" className="a-btn sm ghost a-site-add" onClick={() => setRows([...rows, ['', '']])}>+ Új sor</button>
        <button type="button" className="a-chip a-chipbtn" disabled={hasIsolation} title={`${ISOLATION_HEADERS.join(', ')} — kötelező a / útvonalon (SharedArrayBuffer, szálas WebGL)`} onClick={addIsolation}>+ COOP/COEP/CORP a /-re</button>
      </div>
    </div>
  );
}
